import _ from 'lodash';
import {
    ProviderClaimPayment,
    ProviderProfessionalClaimTemplate,
    SupportClaimPayment,
    SupportProfessionalClaimTemplate,
} from '../../../api';
import { parseAmount } from '../../../services/primitives/number-format';
import { getPaymentItemKey } from '../matching-keys';
import { getPaymentsWithoutReversals } from '../payment-summary';
import { createProfessionalPaymentItemMatcher } from '../professional-payment-matching';
import {
    buildMismatchDescription,
    findLikelyMatchLineNumber,
} from '../record-of-services/record-of-services.utils';

export type ServiceLineMatchLine = {
    line: number;
    code?: string;
    modifiers?: string[];
    dos?: string;
    billed: number;
    allowed: number;
    paid: number;
    matched: boolean;
};

export type UnmatchedPaymentLine = {
    code?: string;
    modifiers?: string[];
    dos?: string;
    paid: number;
    likelyLine?: number;
    reason: string;
};

export type ServiceLineMatchSummary = {
    lines: ServiceLineMatchLine[];
    unmatchedPayments: UnmatchedPaymentLine[];
};

type ProfessionalTemplate = ProviderProfessionalClaimTemplate | SupportProfessionalClaimTemplate;
type ClaimPayment = ProviderClaimPayment | SupportClaimPayment;

type PaymentItemWithMeta = NonNullable<ClaimPayment['items']>[number] & {
    paymentId?: string;
    status?: ClaimPayment['status'];
};

const isProfessionalTemplate = (template: unknown): template is ProfessionalTemplate =>
    !!template && typeof template === 'object' && 'f24ServiceLine' in template;

const formatDos = (from?: string, through?: string): string | undefined => {
    if (from && through && from !== through) {
        return `${from} – ${through}`;
    }
    return from || through || undefined;
};

/**
 * Builds a compact service-line ↔ payment matching summary for professional claims.
 * Uses the same matching primitives as Record of Services so AI context stays consistent with the UI.
 * Returns undefined for non-professional templates (institutional / NYNF3 / missing).
 */
export function buildServiceLineMatchSummary(
    template: unknown,
    payments: ClaimPayment[] | unknown[] | undefined,
): ServiceLineMatchSummary | undefined {
    if (!isProfessionalTemplate(template)) {
        return undefined;
    }

    const serviceLines = template.f24ServiceLine || [];
    const paymentsWithoutReversals = getPaymentsWithoutReversals(
        (payments || []) as ClaimPayment[],
    );

    const existingControlNumbers = serviceLines.map(line => line.x12ControlNumber);
    const paymentItems: PaymentItemWithMeta[] = paymentsWithoutReversals.flatMap(payment =>
        (payment.items || []).map(item => ({
            ...item,
            status: payment.status,
            paymentId: payment.id,
        })),
    );

    const matcher = createProfessionalPaymentItemMatcher(paymentItems, existingControlNumbers);

    const lines: ServiceLineMatchLine[] = serviceLines.map((line, index) => {
        const matchedItems = matcher.matchLine(line);
        const paid = matchedItems.reduce((sum, item) => sum + parseAmount(item.paidAmount), 0);
        const allowedValues = matchedItems.map(item => parseAmount(item.allowedAmount));
        const allowed = allowedValues.length ? Math.max(...allowedValues) : 0;

        return {
            line: index + 1,
            code: line.f24dProcedureCode,
            modifiers: line.f24dProcedureModifiers,
            dos: formatDos(line.f24aFromDateOfService, line.f24aThroughDateOfService),
            billed: parseAmount(line.f24fChargeAmount),
            allowed: Number.isFinite(allowed) ? allowed : 0,
            paid,
            matched: matchedItems.length > 0,
        };
    });

    const usedPaymentItems = matcher.getUsedPaymentItems();
    const unusedPaymentItems = paymentItems.filter(item => !usedPaymentItems.has(item));

    const notFoundByKey = new Map<string, PaymentItemWithMeta[]>();
    unusedPaymentItems.forEach(item => {
        const key = getPaymentItemKey(item, existingControlNumbers) || 'NOT_FOUND';
        const group = notFoundByKey.get(key) || [];
        group.push(item);
        notFoundByKey.set(key, group);
    });

    const unmatchedPayments: UnmatchedPaymentLine[] = [];
    for (const group of notFoundByKey.values()) {
        const first = _.first(group);
        if (!first) {
            continue;
        }

        const paid = group.reduce((sum, item) => sum + parseAmount(item.paidAmount), 0);
        const likelyLine = findLikelyMatchLineNumber(template, first);
        const mismatch = buildMismatchDescription(first, likelyLine, formatDos);

        unmatchedPayments.push({
            code: first.procedureCode,
            modifiers: first.modifierCodes,
            dos: formatDos(first.fromDos, first.throughDos),
            paid,
            likelyLine,
            reason: mismatch.full,
        });
    }

    return { lines, unmatchedPayments };
}
